// =============================================================================
// MeuDriver — BadgeShowcase (Batch S3)
// Grid de emblemas do estudante — earned e locked
// =============================================================================

import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Medal } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { useStudentBadges } from '@/hooks/useStudentBadges'
import type { BadgeWithStatus } from '@/hooks/useStudentBadges'

// ─── BadgeItem ────────────────────────────────────────────────────────────────

interface BadgeItemProps {
  badge: BadgeWithStatus
}

function BadgeItem({ badge }: BadgeItemProps) {
  const tooltipText = badge.earned && badge.earned_at
    ? `Conquistado em ${format(parseISO(badge.earned_at), "d 'de' MMMM 'de' yyyy", { locale: ptBR })}`
    : badge.description ?? ''

  return (
    <div
      className="flex flex-col items-center gap-1.5 group"
      title={tooltipText}
      data-testid={`badge-${badge.name}`}
    >
      <div
        className={cn(
          'relative flex h-16 w-16 items-center justify-center rounded-2xl border-2 text-2xl transition-all duration-300',
          badge.earned
            ? 'border-amber-300 bg-gradient-to-br from-amber-50 to-yellow-50 shadow-md group-hover:shadow-lg group-hover:scale-105'
            : 'border-gray-200 bg-gray-50 grayscale opacity-40',
        )}
      >
        <span role="img" aria-label={badge.name}>
          {badge.icon_emoji}
        </span>
      </div>
      <p
        className={cn(
          'text-center text-[10px] font-semibold leading-tight max-w-[72px] truncate',
          badge.earned ? 'text-gray-700' : 'text-gray-400',
        )}
        title={badge.description ?? badge.name}
      >
        {badge.description?.split(' ').slice(0, 3).join(' ') ?? badge.name}
      </p>
      {badge.earned && badge.earned_at && (
        <p className="text-[9px] text-muted-foreground">
          {format(parseISO(badge.earned_at), 'dd/MM/yy')}
        </p>
      )}
    </div>
  )
}

// ─── BadgeShowcase ────────────────────────────────────────────────────────────

interface Props {
  studentId?: string
  /** Quando true, mostra apenas os badges ganhos */
  earnedOnly?: boolean
}

export function BadgeShowcase({ studentId, earnedOnly = false }: Props) {
  const { data: badges = [], isLoading } = useStudentBadges(studentId)

  const earned    = badges.filter((b) => b.earned)
  const notEarned = badges.filter((b) => !b.earned)
  const displayed = earnedOnly ? earned : badges

  if (isLoading) {
    return (
      <div className="flex flex-wrap gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-16 rounded-2xl" />
        ))}
      </div>
    )
  }

  if (displayed.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-6 text-center text-muted-foreground">
        <Medal className="h-8 w-8 opacity-20" />
        <p className="text-sm">Nenhum emblema ainda.</p>
        {!earnedOnly && (
          <p className="text-xs opacity-70">Complete aulas para desbloquear emblemas.</p>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Resumo */}
      {!earnedOnly && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Medal className="h-4 w-4 text-amber-500" />
          <span>
            <strong className="text-gray-800">{earned.length}</strong> de{' '}
            <strong className="text-gray-800">{badges.length}</strong> emblemas conquistados
          </span>
        </div>
      )}

      {/* Grid */}
      <div className="flex flex-wrap gap-4">
        {displayed.map((badge) => (
          <BadgeItem key={badge.id} badge={badge} />
        ))}
      </div>

      {/* Legenda */}
      {!earnedOnly && notEarned.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Emblemas esmaecidos ainda nao foram conquistados.
        </p>
      )}
    </div>
  )
}
